import React from 'react';
import PropTypes from 'prop-types';
import i18next from 'i18next';
import keycode from 'keycode';
import { get, union, without, isEmpty, isFunction } from 'lodash';
import clxBind from 'classnames/bind';
import List from './List';
import Input from '../Input';
import Button from '../Button';
import styles from './styles.module.scss';

const clx = clxBind.bind(styles);

export default class ComboBox extends React.Component {
  static propTypes = {
    options: PropTypes.array.isRequired,
    labelKey: PropTypes.string.isRequired,
    valueKey: PropTypes.string,
    selectedLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    searchPlaceHolder: PropTypes.string,
    value: PropTypes.array,
    onChange: PropTypes.func,
  };

  static defaultProps = {
    valueKey: undefined,
    searchPlaceHolder: i18next.t('MultiSelectBox:search'),
    selectedLabel: i18next.t('MultiSelectBox:items-selected'),
    value: [],
    onChange: undefined,
  };

  constructor(props) {
    super(props);

    this.state = {
      filterText: '',
      filteredOptions: this.getFilteredOptions(props),
      selectedOptions: this.getSelectedOptions(props),
      lastAvailableIndex: -1,
      lastSelectedIndex: -1,
      toSelect: [],
      toRemove: [],
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState({
      filteredOptions: this.getFilteredOptions(nextProps, get(this.state, 'filterText', '')),
      selectedOptions: this.getSelectedOptions(nextProps),
    });
  }

  getFilteredOptions = (props, filterText = '') => {
    const { valueKey, labelKey, options, value } = props;
    const key = valueKey || labelKey;

    return options
      .filter(item => value.findIndex(v => v === item[key]) < 0)
      .filter(item => item[labelKey].toLocaleLowerCase().includes(filterText.toLocaleLowerCase()));
  };

  getSelectedOptions = props => {
    const { valueKey, labelKey, options, value } = props;
    const key = valueKey || labelKey;

    return options.filter(item => value.includes(item[key]));
  };

  getAvailableOptionKey = index => {
    const { valueKey, labelKey } = this.props;
    const { filteredOptions } = this.state;
    const key = valueKey || labelKey;

    return filteredOptions[index][key];
  };

  getSelectedOptionKey = index => {
    const { valueKey, labelKey } = this.props;
    const { selectedOptions } = this.state;
    const key = valueKey || labelKey;

    return selectedOptions[index][key];
  };

  handleFilterChange = event => {
    this.setState({
      filterText: event.target.value,
      filteredOptions: this.getFilteredOptions(this.props, event.target.value),
    });
  };

  moveOptions = (focusedIndex, selectedValues, options) => {
    const { labelKey, valueKey } = this.props;
    const key = valueKey || labelKey;
    let next;

    // eslint-disable-next-line for-direction
    for (let i = focusedIndex, len = options.length; i < len; i += 1) {
      if (!selectedValues.includes(options[i][key])) {
        next = i;
        break;
      }
    }

    if (!next) {
      next = options.length - selectedValues.length - 1;
    }

    return next >= 0 ? [options[next][key]] : [];
  };

  moveOption = (optionValue, focusedIndex, options) => {
    const { labelKey, valueKey } = this.props;
    const key = valueKey || labelKey;
    const lastIndex = options.length - 1;
    const next = focusedIndex === lastIndex ? lastIndex - 1 : focusedIndex + 1;

    return next >= 0 ? [options[next][key]] : [];
  };

  handleAvailableSelect = selectedValues => this.setState({ toSelect: selectedValues });

  handleAvailableKeyDown = (focusedIndex, selectedValues, event) => {
    if (keycode(event.keyCode) !== 'enter' || focusedIndex === -1) {
      return;
    }

    event.preventDefault();

    const { onChange, value } = this.props;
    const { filteredOptions } = this.state;

    const toSelect = this.moveOptions(focusedIndex, selectedValues, filteredOptions);

    if (onChange) {
      onChange(union(value, selectedValues));
    }

    this.setState({ toSelect });
  };

  handleAvailableOptionDoubleClick = (optionValue, focusedIndex) => {
    const { onChange, value } = this.props;
    const { filteredOptions } = this.state;

    const toSelect = this.moveOption(optionValue, focusedIndex, filteredOptions);

    if (onChange) {
      onChange(union(value, [optionValue]));
    }

    this.setState({ toSelect });
  };

  handleAvailableOptionFocus = focusedIndex => this.setState({ lastAvailableIndex: focusedIndex });

  handleSelectClick = () => {
    const { onChange, value } = this.props;
    const { filteredOptions, toSelect, lastAvailableIndex } = this.state;

    const nextToSelect = this.moveOptions(lastAvailableIndex, toSelect, filteredOptions);

    if (onChange) {
      onChange(union(value, toSelect));
    }

    this.setState({ toSelect: nextToSelect });
  };

  handleSelectAllClick = () => {
    const { onChange, valueKey, labelKey, value } = this.props;
    const { filteredOptions } = this.state;
    const key = valueKey || labelKey;

    if (onChange) {
      onChange(
        union(
          value,
          filteredOptions.map(option => option[key])
        )
      );
    }

    this.setState({ toSelect: [] });
  };

  handleSelectedSelect = selectedValues => this.setState({ toRemove: selectedValues });

  handleSelectedKeyDown = (focusedIndex, selectedValues, event) => {
    if (
      (keycode(event.keyCode) !== 'enter' && keycode(event.keyCode) !== 'delete') ||
      focusedIndex === -1
    ) {
      return;
    }

    event.preventDefault();

    const { onChange, value } = this.props;
    const { selectedOptions } = this.state;

    const toRemove = this.moveOptions(focusedIndex, selectedValues, selectedOptions);

    if (onChange) {
      onChange(without(value, ...selectedValues));
    }

    this.setState({ toRemove });
  };

  handleSelectedOptionDoubleClick = (optionValue, focusedIndex) => {
    const { onChange, value } = this.props;
    const { selectedOptions } = this.state;

    const toRemove = this.moveOption(optionValue, focusedIndex, selectedOptions);

    if (onChange) {
      onChange(without(value, optionValue));
    }

    this.setState({ toRemove });
  };

  handleSelectedOptionFocus = focusedIndex => this.setState({ lastSelectedIndex: focusedIndex });

  handleRemoveClick = () => {
    const { onChange, value } = this.props;
    const { selectedOptions, toRemove, lastSelectedIndex } = this.state;

    const nextToRemove = this.moveOptions(lastSelectedIndex, toRemove, selectedOptions);

    if (onChange) {
      onChange(without(value, ...toRemove));
    }

    this.setState({ toRemove: nextToRemove });
  };

  handleRemoveAllClick = () => {
    const { onChange } = this.props;

    if (onChange) {
      onChange([]);
    }

    this.setState({ toRemove: [] });
  };

  renderOption = (option, optionsProps) => {
    const { labelKey } = this.props;
    const { isFocused, isSelected } = optionsProps;

    const optionClasses = clx('option', {
      focused: isFocused,
      selected: isSelected,
    });

    return <div className={optionClasses}>{option[labelKey]}</div>;
  };

  renderAvailableOption = (index, optionsProps) => {
    const { filteredOptions } = this.state;
    return this.renderOption(filteredOptions[index], optionsProps);
  };

  renderSelectedOption = (index, optionsProps) => {
    const { selectedOptions } = this.state;
    return this.renderOption(selectedOptions[index], optionsProps);
  };

  render() {
    const { searchPlaceHolder, selectedLabel, labelKey, valueKey, value } = this.props;
    const { filterText, filteredOptions, selectedOptions, toSelect, toRemove } = this.state;

    const selectedLabelText = isFunction(selectedLabel)
      ? selectedLabel(value.length)
      : selectedLabel;

    return (
      <div className={styles['wrapper']}>
        <div className={styles['header']}>
          <div className={styles['search-wrapper']}>
            <Input
              type="text"
              value={filterText}
              onChange={this.handleFilterChange}
              placeholder={searchPlaceHolder}
              className={styles['search-input']}
            />
          </div>
          <div className={styles['filler']} />
          <div className={styles['selected-label']}>{selectedLabelText}</div>
        </div>
        <div className={styles['body']}>
          <div className={styles['source-col']}>
            <List
              options={filteredOptions}
              selected={toSelect}
              labelKey={labelKey}
              valueKey={valueKey}
              className={styles['available-options']}
              onSelect={this.handleAvailableSelect}
              onKeyDown={this.handleAvailableKeyDown}
              onOptionDoubleClick={this.handleAvailableOptionDoubleClick}
              onOptionFocus={this.handleAvailableOptionFocus}
            />
          </div>
          <div className={styles['actions-col']}>
            <Button
              onClick={this.handleSelectAllClick}
              icon="angle-double-right"
              className={styles['action-button']}
              disabled={isEmpty(filteredOptions)}
              title={i18next.t('MultiSelectBox:select-all-items')}
            />
            <Button
              onClick={this.handleSelectClick}
              icon="angle-right"
              className={styles['action-button']}
              disabled={isEmpty(toSelect)}
              title={i18next.t('MultiSelectBox:select-items')}
            />
            <Button
              onClick={this.handleRemoveClick}
              icon="angle-left"
              className={styles['action-button']}
              disabled={isEmpty(toRemove)}
              title={i18next.t('MultiSelectBox:remove-items')}
            />
            <Button
              onClick={this.handleRemoveAllClick}
              icon="angle-double-left"
              className={styles['action-button']}
              disabled={isEmpty(selectedOptions)}
              title={i18next.t('MultiSelectBox:remove-all-items')}
            />
          </div>
          <div className={styles['selected-col']}>
            <List
              options={selectedOptions}
              selected={toRemove}
              labelKey={labelKey}
              valueKey={valueKey}
              onSelect={this.handleSelectedSelect}
              onKeyDown={this.handleSelectedKeyDown}
              onOptionDoubleClick={this.handleSelectedOptionDoubleClick}
              onOptionFocus={this.handleSelectedOptionFocus}
            />
          </div>
        </div>
      </div>
    );
  }
}
